import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { CheckCircle2, RotateCcw, Send, X } from 'lucide-react';
import styles from './VlSupportThread.module.css';
import { GET_VL_SUPPORT_TICKET_THREAD } from '../api/supportTicketsQuery';
import {
  completeVlSupportTicket,
  markVlSupportTicketRead,
  reopenVlSupportTicket,
  replyVlSupportTicket,
  setVlSupportTicketOwners,
} from '../../ManageTickets/api/vlListing.api';
import { subscribeVlSupportUpdates } from '../hooks/useVlSupportAlerts';
import { globalContext } from '../../../Provider/AppProvider';
import { deriveOwnerInitials } from '../../ManageTickets/vlSupportEmail';
import { OwnerPicker } from './OwnerPicker';
import { TicketLinks } from './TicketLinks';
import { defaultOwners, ticketOwners } from '../supportOwners';
import {
  SUPPORT_TYPE_LABELS,
  VlSupportTicketRow,
  formatDateTime,
  formatDuration,
  formatFullDateTime,
  syncAccountsSummary,
  ticketOpenSeconds,
} from '../supportTicketUtils';

type Message = {
  vsm_id: string;
  vsm_direction: 'outbound' | 'inbound';
  vsm_from?: string | null;
  vsm_body_text: string;
  vsm_sent_at: string;
  vsm_sent_by_email?: string | null;
  vsm_owner_initials?: string | null;
};

type Event = { vste_id: string; vste_type: string; vste_actor_email?: string | null; vste_created_at: string };

type Thread = VlSupportTicketRow & { messages: Message[]; events: Event[] };

const EVENT_LABELS: Record<string, string> = {
  completed: 'Marked complete',
  reopened: 'Reopened',
};

type Props = {
  ticketId: string;
  onClose?: () => void;
  /** Called after complete / reopen / reply so the parent can refresh its list. */
  onChanged?: () => void;
};

/** Victory Live email thread with reply, complete and reopen. */
export function VlSupportThread({ ticketId, onClose, onChanged }: Props) {
  const apolloClient = useApolloClient();
  const [thread, setThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);
  const { globalState } = useContext<any>(globalContext);
  const myInitials = deriveOwnerInitials(globalState?.profile?.firstName, globalState?.profile?.lastName);
  const [reply, setReply] = useState('');
  // Who is answering this reply. It is logged on the message and added to the people in charge.
  const [replyOwners, setReplyOwners] = useState<string[]>(() => defaultOwners(myInitials));
  const [savingOwners, setSavingOwners] = useState(false);
  const [busy, setBusy] = useState<'reply' | 'complete' | 'reopen' | null>(null);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apolloClient.query({
        query: GET_VL_SUPPORT_TICKET_THREAD,
        variables: { id: ticketId },
        fetchPolicy: 'network-only',
      });
      setThread(data?.vl_support_ticket_by_pk ?? null);
    } catch (error: any) {
      setNote({ kind: 'err', text: error?.message || "Couldn't load this ticket." });
    } finally {
      setLoading(false);
    }
  }, [apolloClient, ticketId]);

  useEffect(() => {
    setLoading(true);
    setThread(null);
    setNote(null);
    void load();
  }, [load]);

  // Opening the thread clears its unread replies.
  useEffect(() => {
    if (thread && Number(thread.vst_unread_count || 0) > 0) {
      markVlSupportTicketRead(thread.vst_id).then(() => onChanged?.(), () => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.vst_id, thread?.vst_unread_count]);

  // Live: a new Victory Live reply on this ticket shows up without a refresh.
  useEffect(
    () =>
      subscribeVlSupportUpdates((update) => {
        if (update.ticketId === ticketId) void load();
      }),
    [ticketId, load],
  );

  const run = async (kind: 'reply' | 'complete' | 'reopen') => {
    if (!thread || busy) return;
    if (kind === 'reply' && !reply.trim()) {
      setNote({ kind: 'err', text: 'Type a reply first.' });
      return;
    }
    setBusy(kind);
    setNote(null);
    try {
      if (kind === 'reply') {
        const result = await replyVlSupportTicket(thread.vst_id, reply.trim(), replyOwners);
        setReply('');
        setNote({
          kind: 'ok',
          text: result.redirectedForNonProd
            ? `Reply sent to ${result.sentTo} (test inbox, not Victory Live).`
            : 'Reply sent to Victory Live.',
        });
      } else if (kind === 'complete') {
        await completeVlSupportTicket(thread.vst_id);
        setNote({ kind: 'ok', text: 'Ticket marked complete.' });
      } else {
        await reopenVlSupportTicket(thread.vst_id);
        setNote({ kind: 'ok', text: 'Ticket reopened.' });
      }
      await load();
      onChanged?.();
    } catch (error: any) {
      setNote({ kind: 'err', text: error?.message || 'Something went wrong.' });
    } finally {
      setBusy(null);
    }
  };

  const changeInCharge = async (next: string[]) => {
    if (!thread || savingOwners) return;
    setSavingOwners(true);
    setNote(null);
    try {
      await setVlSupportTicketOwners(thread.vst_id, next);
      await load();
      onChanged?.();
    } catch (error: any) {
      setNote({ kind: 'err', text: error?.message || "Couldn't update who is in charge." });
    } finally {
      setSavingOwners(false);
    }
  };

  if (loading) return <div className={styles.empty}>Loading ticket…</div>;
  if (!thread) {
    return (
      <div className={styles.thread}>
        {note && <div className={`${styles.note} ${styles.noteErr}`}>{note.text}</div>}
        <div className={styles.empty}>This ticket couldn't be found.</div>
      </div>
    );
  }

  const isOpen = thread.vst_status === 'open';
  // The first time Victory Live wrote to us on this ticket (for one they started, that is when it began).
  const firstFromVl = thread.messages.find((m) => m.vsm_direction === 'inbound')?.vsm_sent_at ?? null;
  const details = [
    thread.vst_event_date,
    thread.vst_pid ? `PID ${thread.vst_pid}` : null,
    thread.vst_section ? `Sec ${thread.vst_section}` : null,
    thread.vst_row ? `Row ${thread.vst_row}` : null,
    thread.vst_seats ? `Seats ${thread.vst_seats}` : null,
  ].filter(Boolean);

  // Interleave status changes with messages so the history reads in order.
  const timeline = [
    ...thread.messages.map((m) => ({ at: m.vsm_sent_at, message: m as Message | null, event: null as Event | null })),
    ...thread.events
      .filter((e) => EVENT_LABELS[e.vste_type])
      .map((e) => ({ at: e.vste_created_at, message: null as Message | null, event: e as Event | null })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  return (
    <div className={styles.thread}>
      <div className={styles.header}>
        <div>
          <p className={styles.title}>{thread.vst_event_name || thread.vst_subject}</p>
          <div className={styles.meta}>{details.join(' · ')}</div>
        </div>
        {onClose && (
          <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        )}
      </div>

      <div className={styles.chips}>
        <span className={`${styles.chip} ${isOpen ? styles.chipOpen : styles.chipDone}`}>
          {isOpen ? 'Open' : 'Completed'} · {formatDuration(ticketOpenSeconds(thread))}
        </span>
        <span className={styles.chip}>{SUPPORT_TYPE_LABELS[thread.vst_type]}</span>
        {(thread.vst_marketplaces || []).length > 0 && (
          <span className={styles.chip}>{(thread.vst_marketplaces || []).join(', ')}</span>
        )}
        {thread.vst_type === 'barcode_sync' && syncAccountsSummary(thread.vst_sync_accounts) && (
          <span className={styles.chip}>{syncAccountsSummary(thread.vst_sync_accounts)}</span>
        )}
        {Number(thread.vst_reopen_count || 0) > 0 && (
          <span className={styles.chip}>Reopened {thread.vst_reopen_count}×</span>
        )}
      </div>

      {firstFromVl && (
        <div className={styles.meta}>
          <strong>Victory Live first emailed us:</strong> {formatFullDateTime(firstFromVl)}
        </div>
      )}

      {thread.vst_type === 'inbound' && (
        <>
          <div className={styles.meta}>
            From {thread.vst_contact_name ? `${thread.vst_contact_name} ` : ''}
            {thread.vst_contact_email ? `<${thread.vst_contact_email}>` : 'Victory Live'}. Replies go back to this address.
          </div>
          <TicketLinks
            ticketId={thread.vst_id}
            userId={thread.vst_user_id}
            eventId={thread.vst_event_id}
            eventName={thread.vst_event_name}
            onChanged={() => {
              void load();
              onChanged?.();
            }}
          />
        </>
      )}

      <div className={styles.inCharge}>
        <span className={styles.inChargeLabel}>In charge</span>
        <OwnerPicker
          value={ticketOwners(thread)}
          onChange={(next) => void changeInCharge(next)}
          myInitials={myInitials}
          disabled={savingOwners}
          label="Who is in charge of this ticket"
        />
      </div>

      <div className={styles.messages}>
        {timeline.map((item) =>
          item.message ? (
            <div
              key={item.message.vsm_id}
              className={`${styles.message} ${item.message.vsm_direction === 'inbound' ? styles.messageInbound : ''}`}
            >
              <div className={styles.messageHead}>
                <span>
                  {item.message.vsm_direction === 'inbound'
                    ? item.message.vsm_from || 'Victory Live'
                    : `QuickAsyst${item.message.vsm_owner_initials ? ` (${item.message.vsm_owner_initials})` : ''}`}
                </span>
                <span>{formatDateTime(item.message.vsm_sent_at)}</span>
              </div>
              <div className={styles.messageBody}>{item.message.vsm_body_text}</div>
            </div>
          ) : (
            <div key={item.event!.vste_id} className={styles.system}>
              {EVENT_LABELS[item.event!.vste_type]}
              {item.event!.vste_actor_email ? ` by ${item.event!.vste_actor_email}` : ''} ·{' '}
              {formatDateTime(item.event!.vste_created_at)}
            </div>
          ),
        )}
        {!thread.messages.length && <div className={styles.empty}>No messages yet.</div>}
      </div>

      <div className={styles.inCharge}>
        <span className={styles.inChargeLabel}>Replying as</span>
        <OwnerPicker value={replyOwners} onChange={setReplyOwners} myInitials={myInitials} disabled={!!busy} label="Who is replying" />
      </div>
      <textarea
        className={styles.reply}
        placeholder="Write a reply to Victory Live…"
        value={reply}
        onChange={(event) => setReply(event.target.value)}
        aria-label="Reply to Victory Live"
      />
      <div className={styles.actions}>
        <button type="button" className={styles.btnPrimary} onClick={() => run('reply')} disabled={!!busy}>
          <Send size={13} /> {busy === 'reply' ? 'Sending…' : 'Reply'}
        </button>
        <span className={styles.spacer} />
        {isOpen ? (
          <button type="button" className={styles.btnComplete} onClick={() => run('complete')} disabled={!!busy}>
            <CheckCircle2 size={13} /> Mark complete
          </button>
        ) : (
          <button type="button" className={styles.btn} onClick={() => run('reopen')} disabled={!!busy}>
            <RotateCcw size={13} /> Reopen ticket
          </button>
        )}
      </div>
      {note && (
        <div className={`${styles.note} ${note.kind === 'ok' ? styles.noteOk : styles.noteErr}`} role="status">
          {note.text}
        </div>
      )}
    </div>
  );
}
